import React, { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import {
  CheckCircle2, Loader2, AlertTriangle, Target, Users, Gift, Radio,
  Clock, ListChecks,
} from "lucide-react";
import { api } from "../lib/api";
import { priorityLabel } from "../lib/whatsappMessage";

/**
 * Public, no-login "shared task" execution view (C-ORE Task + Playbook +
 * WhatsApp Execution System, 2026-08). An employee lands here straight
 * from a WhatsApp link — no C-ORE account, no password. Every write here
 * (checklist tick, "Mark Task Complete") updates the SAME canonical task
 * Command Center/CMO already show — never a separate employee status.
 */
const PRIORITY_STYLE = {
  High: "bg-[#B44554] text-white", Medium: "bg-amber-100 text-amber-700", Low: "bg-slate-100 text-slate-600",
};

function Section({ icon, title, children, testid }) {
  return (
    <div data-testid={testid} className="rounded-xl border border-slate-200 bg-white p-4">
      <div className="flex items-center gap-1.5 text-[11px] font-bold uppercase tracking-wider text-slate-500">
        {icon} {title}
      </div>
      <div className="mt-2">{children}</div>
    </div>
  );
}

export default function SharedTaskPage() {
  const { token } = useParams();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [data, setData] = useState(null);
  const [name, setName] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [checklistBusy, setChecklistBusy] = useState(null);
  const [completeError, setCompleteError] = useState("");

  const load = async () => {
    setLoading(true);
    const res = await api.sharedGetTask(token);
    setLoading(false);
    if (!res.ok) {
      setError(res.status === 404
        ? "This link is invalid, expired, or has been revoked. Please ask your manager to re-share the task."
        : res.error || "Could not load this task right now. Please try again.");
      return;
    }
    setData(res.data);
    setName(res.data.employee_name || "");
  };

  useEffect(() => { load(); }, [token]); // eslint-disable-line

  const toggleChecklist = async (idx, current) => {
    setChecklistBusy(idx);
    setData((d) => ({
      ...d,
      checklist: d.checklist.map((c, i) => (i === idx ? { ...c, done: !current } : c)),
    }));
    const res = await api.sharedToggleChecklist(token, idx, !current);
    setChecklistBusy(null);
    if (res.ok) {
      setData((d) => ({ ...d, checklist: res.data.checklist, checklist_progress: res.data.checklist_progress }));
    }
  };

  const markComplete = async () => {
    if (!name.trim() || submitting) return;
    setCompleteError("");
    setSubmitting(true);
    const res = await api.sharedCompleteTask(token, name.trim());
    setSubmitting(false);
    if (res.ok) {
      setData((d) => ({ ...d, completed: true, employee_name: name.trim() }));
    } else {
      setCompleteError(
        res.status === 404
          ? "This task could not be found — it may have been removed. Please ask your manager to re-share it."
          : res.error || "Could not mark this task complete. Please try again."
      );
    }
  };

  if (loading) {
    return (
      <div data-testid="shared-task-loading" className="flex min-h-screen items-center justify-center bg-slate-50 px-4">
        <div className="text-center text-slate-500">
          <Loader2 className="mx-auto mb-2 h-6 w-6 animate-spin" />
          <p className="text-[13px]">Loading task…</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div data-testid="shared-task-error" className="flex min-h-screen items-center justify-center bg-slate-50 px-4">
        <div className="w-full max-w-sm rounded-xl border border-red-200 bg-white p-6 text-center shadow-card">
          <AlertTriangle className="mx-auto h-8 w-8 text-red-500" />
          <p className="mt-3 text-[13px] text-slate-600">{error}</p>
        </div>
      </div>
    );
  }

  if (!data) return null;

  const { task, playbook, checklist, checklist_progress, completed } = data;
  const pr = priorityLabel(task.priority);

  return (
    <div data-testid="shared-task-page" className="min-h-screen bg-slate-50 px-4 py-6 tablet:py-10">
      <div className="mx-auto max-w-md space-y-3">
        <div className="text-center">
          <span className="text-[15px] font-display font-bold tracking-wide text-blue-800">C-ORE</span>
          <p className="mt-0.5 text-[10.5px] font-bold uppercase tracking-wider text-slate-400">Task Assigned</p>
        </div>

        <div className="rounded-xl border border-slate-200 bg-white p-4 shadow-card" data-testid="shared-task-header">
          <h1 className="text-[16px] font-bold text-[#0F1B2D]">{task.title}</h1>
          <div className="mt-2 flex flex-wrap items-center gap-2">
            {pr && <span className={`rounded-full px-2 py-0.5 text-[10.5px] font-bold ${PRIORITY_STYLE[pr] || PRIORITY_STYLE.Medium}`}>{pr}</span>}
            {task.due_timeline && (
              <span className="inline-flex items-center gap-1 text-[11.5px] text-slate-500"><Clock size={12} /> Due: {task.due_timeline}</span>
            )}
          </div>
          <p className="mt-2 text-[11.5px] text-slate-400">Assigned by: {task.assigned_by}</p>
        </div>

        {task.detail && (
          <Section icon={<Target size={13} />} title="What To Do" testid="shared-task-detail">
            <p className="text-[13px] text-slate-700">{task.detail}</p>
          </Section>
        )}

        {playbook && (
          <Section icon={<ListChecks size={13} />} title="Playbook" testid="shared-task-playbook">
            <div className="space-y-2 text-[12.5px] text-slate-700">
              {playbook.objective && <p><span className="font-semibold text-slate-500">Objective: </span>{playbook.objective}</p>}
              {playbook.audience && <p className="flex items-start gap-1.5"><Users size={13} className="mt-0.5 shrink-0 text-slate-400" />{playbook.audience}</p>}
              {playbook.offer && <p className="flex items-start gap-1.5"><Gift size={13} className="mt-0.5 shrink-0 text-slate-400" />{playbook.offer}</p>}
              {playbook.channels?.length > 0 && (
                <div className="flex flex-wrap gap-1.5">
                  {playbook.channels.map((c, i) => (
                    <span key={i} className="inline-flex items-center gap-1 rounded-full bg-slate-100 px-2 py-0.5 text-[11px] text-slate-600"><Radio size={10} />{c}</span>
                  ))}
                </div>
              )}
            </div>
          </Section>
        )}

        {checklist?.length > 0 && (
          <Section icon={<CheckCircle2 size={13} />} title="Execution Checklist" testid="shared-task-checklist">
            <p className="mb-2 text-[11.5px] font-semibold text-slate-500" data-testid="shared-task-checklist-progress">
              {checklist_progress.done} / {checklist_progress.total} complete
            </p>
            <div className="space-y-1.5">
              {checklist.map((c, i) => (
                <label key={i} data-testid={`shared-task-checklist-${i}`} className="flex items-center gap-2 text-[12.5px] text-slate-700">
                  <input type="checkbox" checked={!!c.done} disabled={checklistBusy === i || completed}
                    onChange={() => toggleChecklist(i, c.done)} className="h-3.5 w-3.5 accent-[#B44554]" />
                  <span className={c.done ? "text-slate-400 line-through" : ""}>{c.label}</span>
                </label>
              ))}
            </div>
          </Section>
        )}

        {completed ? (
          <div className="rounded-xl border border-status-onTrack-border bg-status-onTrack-bg p-4 text-center" data-testid="shared-task-completed">
            <CheckCircle2 className="mx-auto h-6 w-6 text-status-onTrack" />
            <p className="mt-2 text-[13px] font-semibold text-status-onTrack-text">
              Completed{data.employee_name ? ` by ${data.employee_name}` : ""}.
            </p>
          </div>
        ) : (
          <div className="rounded-xl border border-slate-200 bg-white p-4 space-y-2.5" data-testid="shared-task-complete-form">
            <label className="text-[11px] font-semibold uppercase tracking-wider text-slate-400">Your Name</label>
            <input value={name} onChange={(e) => setName(e.target.value)}
              placeholder="e.g. Rahul"
              className="w-full rounded-lg border border-slate-200 p-2.5 text-[13px] focus:outline-none focus:ring-2 focus:ring-blue-800/30"
              data-testid="shared-task-name-input" />
            <button onClick={markComplete} disabled={!name.trim() || submitting}
              className="w-full inline-flex min-h-[44px] items-center justify-center gap-1.5 rounded-lg bg-status-onTrack px-4 py-3 text-[13px] font-semibold text-white hover:bg-status-onTrack-text disabled:opacity-50"
              data-testid="shared-task-mark-complete">
              {submitting ? <Loader2 className="h-4 w-4 animate-spin" /> : <CheckCircle2 className="h-4 w-4" />}
              {submitting ? "Marking complete…" : "Mark Task Complete"}
            </button>
            {completeError && (
              <p className="flex items-start gap-1.5 text-[12px] font-medium text-red-600" data-testid="shared-task-complete-error">
                <AlertTriangle size={14} className="mt-0.5 shrink-0" /> {completeError}
              </p>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
